import type { LngLat } from '../shared/types';
import { bearing, cumulativeDistances, distance, projectToSegment } from './geo';

export interface SnapResult {
  /** ルート上にスナップした位置 */
  point: LngLat;
  /** 生の GPS 位置からスナップ位置までの距離 [m] */
  offset: number;
  /** スナップした線分のインデックス（geometry[i]→geometry[i+1]） */
  segmentIndex: number;
  /** ルート始点からの距離 [m] */
  along: number;
  /** その地点での進行方向 [度] */
  bearing: number;
}

/**
 * 「ルートの線上にスナップ」する軽量マップマッチング。
 * 道路網全体ではなく、現在のルートだけを対象にする（ナビ中はこれで十分）。
 *
 * 前回のセグメント付近だけを探索するので O(窓サイズ)。
 * 後戻り（逆走や U ターン）は少しだけ許す。
 */
export class RouteSnapper {
  private readonly cum: number[];
  private lastIndex = 0;

  constructor(
    private readonly line: LngLat[],
    private readonly opts = { lookBehind: 3, lookAhead: 40 },
  ) {
    if (line.length < 2) throw new Error('route geometry needs >= 2 points');
    this.cum = cumulativeDistances(line);
  }

  get totalDistance(): number {
    return this.cum[this.cum.length - 1];
  }

  reset(): void {
    this.lastIndex = 0;
  }

  snap(p: LngLat): SnapResult {
    const from = Math.max(0, this.lastIndex - this.opts.lookBehind);
    const to = Math.min(this.line.length - 2, this.lastIndex + this.opts.lookAhead);
    let best: SnapResult | null = null;

    for (let i = from; i <= to; i++) {
      const a = this.line[i];
      const b = this.line[i + 1];
      const { point, t } = projectToSegment(p, a, b);
      const offset = distance(p, point);
      if (!best || offset < best.offset) {
        best = {
          point,
          offset,
          segmentIndex: i,
          along: this.cum[i] + (this.cum[i + 1] - this.cum[i]) * t,
          bearing: bearing(a, b),
        };
      }
    }
    // from..to は必ず1つ以上あるので best は非 null
    this.lastIndex = best!.segmentIndex;
    return best!;
  }

  /** ルート始点から along [m] の地点 */
  pointAt(along: number): LngLat {
    const d = Math.max(0, Math.min(this.totalDistance, along));
    let i = 0;
    while (i < this.cum.length - 2 && this.cum[i + 1] < d) i++;
    const seg = this.cum[i + 1] - this.cum[i];
    const t = seg === 0 ? 0 : (d - this.cum[i]) / seg;
    const a = this.line[i], b = this.line[i + 1];
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  }
}
